import {
  Autocomplete,
  Box,
  Dialog,
  DialogActions,
  DialogContent,
} from "@mui/material";
import Grid from "@mui/material/Grid";
import {
  DateHelper,
  offsetTimezone,
} from "../../../../Component/Model/helpers";
import { SupportingDocuments } from "../../../Common/SupportingDocuments/SupportingDocuments";
import { EEPlanObjective } from "../../../../Component/Model/Dto/EmploymentEquity/Plan/EEObjective";
import { toast } from "react-toastify";
import { NotesTable } from "../../../Common/Notes/NotesTable";
import AttachFileIcon from "@mui/icons-material/AttachFile";
import { Controller, SubmitHandler, useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { eePlanObjectiveSchema } from "./EEPlanObjectiveValidation";
import TextField from "@mui/material/TextField";
import { LoadingButton } from "@mui/lab";
import { MDBox } from "../../../../Component/Common/Components/MDBox";
import { MDTypography } from "../../../../Component/Common/Components/MDTypography";
import { AppQuill } from "../../../../Component/Common/Components/FormValidationInputs/AppQuill";
import { MDButton } from "../../../../Component/Common/Components/MDButton";
import { CustomDialogTitle } from "../../../Common/Custom/CustomDialogTitle";
import { useEffect, useState } from "react";
import { ObjectivesService } from "../../../../Component/Api/EmploymentEquity/Plan/Objectives/ObjectivesService";
import { getDateWithoutTime } from "../../../../Helpers/DateFormatter";
import { UserActivityService } from "../../../../Component/Api/UserActivity/UserActivityService";
import { ObjectivesSection } from "../../../../Component/Common/Constants/UserActivityTrackingSections";
import { UserActivityActionType } from "../../../../Component/Model/Enum/BEE123Enums";

interface IProps {
  id?: string;
  scorecardId: string;
  link: string;
  previewPopup: boolean;
  handleCancel: () => void;
  refreshTable: () => void;
  setObjectiveId: (id: string) => void;
}

export const EEPlanObjectiveModal = (props: IProps) => {
  const [documentsPopUp, setDocumentsPopUp] = useState<boolean>(false);
  const [objectiveYears, setObjectiveYears] = useState<
    EEPlanObjective.YearDtoIn[]
  >([]);
  const [selectedDate, setSelectedDate] = useState<string>("");
  const [objectiveId, setObjectiveId] = useState<string | undefined>(props.id);

  const descriptionMaxLength: number = 2000;

  const scorecardElement = 22;
  const noteCaptureSheet = 35;

  const {
    control,
    reset,
    handleSubmit,
    formState: { errors, isSubmitting },
    setValue,
    getValues,
  } = useForm<EEPlanObjective.DtoOut>({
    mode: "all",
    defaultValues: {
      scorecardId: props.scorecardId,
      toDate: getDateWithoutTime(new Date()),
      fromDate: getDateWithoutTime(new Date()),
    },
    resolver: yupResolver(eePlanObjectiveSchema),
  });

  const setDate = (start: string, end: string) => {
    const startDate: Date = new Date(start);
    const endDate: Date = new Date(end);

    setSelectedDate(`${DateHelper(startDate)} - ${DateHelper(endDate)}`);
  };

  const objectivesLog = (actionType: number, actionDescription: string) => {
    UserActivityService.createActivityLog({
        section: ObjectivesSection,
        actionType: actionType,
        actionDescription: actionDescription,
        scorecardId: props.scorecardId,
        requestUrl: window.location.href
    });
  }

  const handleDataSubmit: SubmitHandler<EEPlanObjective.DtoOut> = async (
    objective: EEPlanObjective.DtoOut
  ) => {
    objective.fromDate = offsetTimezone(
      new Date(objective.fromDate.toDateString())
    );
    objective.toDate = offsetTimezone(
      new Date(objective.toDate.toDateString())
    );
    try {
      if (!objectiveId) {
        const result = await ObjectivesService.CreateObjective(objective);
        toast.success("Objective added succesfully");
        objectivesLog(UserActivityActionType.Create, "Add an objective")
        const saveObjectiveId = result.data.id;
        setObjectiveId(saveObjectiveId);
        setValue("id", saveObjectiveId);
        props.setObjectiveId(saveObjectiveId);
        setObjectiveId(saveObjectiveId);
      } else {
        await ObjectivesService.UpdateObjective(objective);
        objectivesLog(UserActivityActionType.Update, "Edit an objective")
        toast.success("Objective saved succesfully");
      }
    } catch (error) {
      console.log(error);
    }
  };

  const handleCancel = () => {
    props.handleCancel();
    setSelectedDate("");
    props.refreshTable();
  };
  const openDocumentsPopUp = () => {
    setDocumentsPopUp(true);
  };

  const getObjectiveYears = async () => {
    try {
      let result = await ObjectivesService.GetYears(props.scorecardId);
      setObjectiveYears(result.data);
    } catch (error) {
      console.log(error);
    }
  };

  const getObjective = async (id: string) => {
    try {
      let result = await ObjectivesService.GetObjective(id);
      var objective = {
        id: result.data.id,
        yearId: result.data.yearId,
        scorecardId: result.data.scorecardId,
        description: result.data.description,
        fromDate: new Date(result.data.fromDate),
        toDate: new Date(result.data.toDate),
      } as EEPlanObjective.DtoOut;
      reset(objective);
      setDate(result.data.fromDate, result.data.toDate);
    } catch (error) {
      console.log(error);
    }
  };

  useEffect(() => {
    if (objectiveId) {
      getObjective(objectiveId);
    }
    getObjectiveYears();

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <Box>
      <Dialog
        open={props.previewPopup}
        onClose={handleCancel}
        aria-labelledby="form-dialog-title"
        maxWidth="md"
        fullWidth
      >
        <CustomDialogTitle id="customized-dialog-title" onClose={handleCancel}>
          {props.id !== undefined ? "Edit Objective" : "Add Objective"}
        </CustomDialogTitle>
        <DialogContent>
          <Grid container justifyContent="center" sx={{ my: 2 }}>
            <Grid item xs={10} lg={10}>
              <Box
                component="form"
                id="PlanObjectiveForm"
                onSubmit={handleSubmit(handleDataSubmit)}
              >
                <MDBox>
                  <MDBox mb={1}>
                    <Grid item xs={12}>
                      <MDBox mb={2}>
                        <Controller
                          control={control}
                          name="yearId"
                          render={({ field: { onChange, value } }) => (
                            <Autocomplete
                              options={objectiveYears}
                              isOptionEqualToValue={(option, value) =>
                                option.id === value.id
                              }
                              value={
                                objectiveYears.find((x) => x.id === value)
                                  ? objectiveYears.find((x) => x.id === value)
                                  : value != null
                                  ? {
                                      id: value,
                                      description: `Year ${value}`,
                                      yearStart: null,
                                      yearEnd: null,
                                    }
                                  : null
                              }
                              getOptionLabel={(option) => option.description}
                              onChange={(e: any, newValue: any) => {
                                onChange(newValue?.id);
                                setValue("yearId", newValue?.id);
                                if (newValue) {
                                  setDate(newValue.yearStart, newValue.yearEnd);
                                }
                              }}
                              renderOption={(props, option) => {
                                return (
                                  <li {...props} key={option.id}>
                                    {option.description}
                                  </li>
                                );
                              }}
                              renderInput={(params) => (
                                <TextField
                                  label="Year *"
                                  {...params}
                                  type="text"
                                  error={!!errors.yearId}
                                  helperText={errors.yearId?.message}
                                  variant="standard"
                                />
                              )}
                            />
                          )}
                        />
                      </MDBox>
                      <MDBox mb={2}>
                        <TextField
                          value={selectedDate}
                          InputLabelProps={{ shrink: true }}
                          type="text"
                          label="Date"
                          fullWidth
                          variant="standard"
                          size="small"
                          disabled
                        />
                      </MDBox>
                      <MDBox mb={2} className="leftAlign">
                        <AppQuill control={control} name='description' label='Description*' maxLength={descriptionMaxLength} />
                      </MDBox>
                    </Grid>
                  </MDBox>
                </MDBox>
              </Box>
              {objectiveId !== undefined && (
                <MDBox style={{ marginLeft: "-40px", marginRight: "-40px" }}>
                  <NotesTable
                    scorecardElement={scorecardElement}
                    noteCaptureSheet={noteCaptureSheet}
                    linkedEntityId={objectiveId}
                    scorecardId={props.scorecardId}
                    section={ObjectivesSection}
                  ></NotesTable>
                </MDBox>
              )}
            </Grid>
          </Grid>
          {objectiveId !== undefined && (
            <SupportingDocuments
              display={documentsPopUp}
              handleCancel={() => setDocumentsPopUp(false)}
              link={props.link}
              id={objectiveId}
              scorecardId={props.scorecardId}
              isEEModule={true}
            />
          )}
        </DialogContent>
        <DialogActions>
          {objectiveId !== undefined && (
            <MDButton
              variant="contained"
              color="primary"
              onClick={openDocumentsPopUp}
              startIcon={<AttachFileIcon />}
              size="medium"
            >
              Attach File
            </MDButton>
          )}
          <LoadingButton
            variant="contained"
            color="primary"
            type="submit"
            form="PlanObjectiveForm"
            loading={isSubmitting}
          >
            Save
          </LoadingButton>
        </DialogActions>
      </Dialog>
    </Box>
  );
};
